import { Form } from 'react-bootstrap';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import { useFormik } from 'formik';
import SwalAlert from '@components/Shared/Alert/SwalAlert';
import { useAppDispatch } from '@store/hooks';
import { updatReturnsTransactions } from '@store/Sales/SalesReturns/salesReturnsSlice';
import { useTranslation } from 'react-i18next';
import UpdateReturnLines from './UpdateReturnLines';
import { AddSaleReturnsSchema } from '../../../../ValidationForm/validationSchema';
import confirmAction from '@components/Shared/Alert/confirmAction';
import {
  DeleteReturnsPurchaseOrder,
  // getReturnsPurchaseOrderById,
  getReturnsPurchaseOrderByPurchasesOrderId
} from '@store/purchases/ReturnsPurchaseOrder/returnsPurchaseOrderSlice';
import { useParams } from 'react-router';
import ButtonPrint from '@components/Print/ButtonPrint';

type ReturnFormProps = {
  data: any;
};

const toNum = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const UpdateReturn = ({ data }: ReturnFormProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { Id } = useParams();

  const formik = useFormik({
    initialValues: {
      id: data.id ?? null,

      no: data.no ?? '',
      code: data.code ?? '',
      organizationId: data.organizationId ?? '',
      peopleId: data.peopleId ?? '',
      recipientname: data.recipientname ?? '',

      billToLocationId: data.billToLocationId ?? null,
      shipToLocationId: data.shipToLocationId ?? null,

      // ✅ مهم: المرجع (فاتورة الأصل) وليس ID المردود نفسه
      referenceId: data.referenceId ?? null,

      transactionDate: data.transactionDate?.split('T')[0] ?? '',
      deliveryDate: data.deliveryDate ?? '',

      expirationDate: data.expirationDate ?? '',
      dueDate: data.dueDate ?? '',

      paymentTermsId: data.paymentTermsId ?? null,
      paymentMethodId: data.paymentMethodId ?? null,

      description: data.description ?? '',

      grossSale: data.grossSale ?? '',
      discountRate: data.discountRate ?? '',
      discountAmount: data.discountAmount ?? '',
      vat: data.vat ?? '',
      netSale: data.netSale ?? '',

      contractId: data.contractId ?? null,
      ameStatus: data.ameStatus ?? 0,

      personId: data.personId ?? null,
      paymentTerms: data.paymentTerms ?? '',
      deliveryTerm: data.deliveryTerm ?? '',
      inventoryId: data.inventoryId ?? '',
      waybill: data.waybill ?? '',
      phone: data.phone ?? '',
      currencyCode: data.currencyCode ?? '',
      currencyConversionRate: data.currencyConversionRate ?? 0,
      batchId: data.batchId ?? null,

      // ✅ الأهم: إضافة ReturnQty + selectedRow مثل مكون الإضافة
      transactionsLinesList:
        data?.transactionsLinesList?.map((item: any) => ({
          id: item.id,
          transactionId: item.transactionId ?? data.id ?? null,

          itemId: item.itemId,
          lineNumber: item.lineNumber,
          uom: item.uom,
          description: item.description,
          price: item.price,
          qty: item.qty,

          discountRate: item.discountRate,
          discountAmount: item.discountAmount,
          vatRate: item.vatRate,

          total: item.total,
          vatAmount: item.vatAmount,

          inventoryId: item.inventoryId,

          // ✅ افتراضيًا: اعتبر السطر داخل الفاتورة الحالية مختار للتعديل
          selectedRow: true,
          // ✅ ReturnQty يبدأ بقيمة qty الحالية (لأنها فاتورة مردود موجودة)
          ReturnQty: item.qty ?? 0
        })) ?? []
    },

    enableReinitialize: true,
    validationSchema: AddSaleReturnsSchema(t),

    onSubmit: values => {
      if (data.inventoryFlag) {
        SwalAlert({
          text: 'عذرًا، لا يمكن تعديل مردود المشتريات بعد صرف الكميات من المخزن. نشكرك على تفهمك.'
        });
        formik.setSubmitting(false);
        return;
      }

      const selected = (values.transactionsLinesList ?? []).filter((x: any) => x?.selectedRow === true);

      if (selected.length === 0) {
        SwalAlert({ text: 'حدد الأصناف للإسترداد' });
        formik.setSubmitting(false);
        return;
      }

      // ✅ تحقق أن ReturnQty > 0 للأسطر المختارة
      const invalidQty = selected.find((x: any) => toNum(x.ReturnQty) <= 0);
      if (invalidQty) {
        SwalAlert({ text: 'تأكد من إدخال كمية مردود أكبر من صفر للأسطر المحددة.' });
        formik.setSubmitting(false);
        return;
      }

      // (اختياري) حساب إجمالي من الأسطر المختارة فقط
      const grossSale = selected.reduce((s: number, a: any) => s + toNum(a.total), 0);

      dispatch(
        updatReturnsTransactions({
          // ✅ لو الـ API يحتاج ID في التعديل
          id: values.id,

          organizationId: values.organizationId,
          peopleId: values.peopleId,
          recipientname: values.recipientname,
          billToLocationId: values.billToLocationId,
          shipToLocationId: values.shipToLocationId,

          referenceId: values.referenceId,

          transactionDate: values.transactionDate,
          deliveryDate: values.deliveryDate || null,
          expirationDate: values.expirationDate || null,
          dueDate: values.dueDate || null,

          paymentTermsId: values.paymentTermsId || null,
          paymentMethodId: values.paymentMethodId || null,

          description: values.description,

          // ✅ نفس منطقك السابق لكن للأسطر المختارة فقط
          grossSale: grossSale,
          discountRate: values.discountRate || 0,
          discountAmount: values.discountAmount || 0,
          vat: values.vat || 0,
          netSale: values.netSale || 0,

          contractId: values.contractId || null,
          ameStatus: values.ameStatus || 0,

          personId: values.personId,
          paymentTerms: values.paymentTerms,
          deliveryTerm: values.deliveryTerm,
          inventoryId: values.inventoryId,
          waybill: values.waybill,
          phone: values.phone,
          currencyCode: values.currencyCode,
          currencyConversionRate: values.currencyConversionRate || 0,
          batchId: values.batchId || null,

          transactionsLinesList: selected.map((item: any, index: number) => ({
            id: item.id,
            transactionId: item.transactionId ?? values.id ?? null,

            itemId: item.itemId,
            lineNumber: index + 1,

            uom: item.uom,
            description: item.description,

            price: toNum(item.price),
            qty: toNum(item.ReturnQty), // ✅ المهم

            discountRate: toNum(item.discountRate),
            discountAmount: toNum(item.discountAmount),

            vatRate: toNum(item.vatRate),
            total: toNum(item.total),
            vatAmount: toNum(item.vatAmount),

            // codeCombinationId: item.codeCombinationId ?? '54F915DF-8A22-486D-B860-08DC2A376B50',
            codeCombinationId: null,

            inventoryId: values.inventoryId
          }))
        })
      )
        .unwrap()
        .then((res: any) => {
          if (res?.succeeded === true) {
            CustomAlert({ action: 'Add' });
            formik.resetForm();
          } else {
            formik.setSubmitting(false);
            CustomAlert({ action: 'Error', msg: res?.message });
          }
        })
        .catch(() => {
          formik.setSubmitting(false);
          CustomAlert({ action: 'Error' });
        });
    }
  });
  const { values, handleChange: formikhandleChange, errors, touched } = formik;

  const handleDelete = async () => {
    const isConfirmed = await confirmAction('هل أنت متأكد من حذف مردود الشراء');

    if (!isConfirmed) return;

    try {
      const result = await dispatch(DeleteReturnsPurchaseOrder(data.id)).unwrap();
      if (result?.succeeded) {
        dispatch(getReturnsPurchaseOrderByPurchasesOrderId(Id ?? ''));

        CustomAlert({ action: 'Delete', msg: t('تم حذف مردود الشراء بنجاح') });
      }
    } catch (e: any) {
      CustomAlert({ action: 'Error', msg: e?.message || t('فشل حذف مردود الشراء') });
    }
  };

  return (
    <div>
      <Form onSubmit={formik.handleSubmit} className='p-1'>
        <div className='row'>
          <div className='col-lg-2'>
            <div className='form-group flex-nowrap mb-3'>
              <label className='form-label'>{t('الرقم')}</label>

              <div className='input-group flex-nowrap'>
                <Form.Control
                  readOnly
                  autoComplete='off'
                  className='form-control fw-bold text-center'
                  type='text'
                  name='no'
                  onChange={formikhandleChange}
                  value={values.code || ''} // إذا تبغاه رقم فقط: values.no
                  isInvalid={!!(touched.no && errors.no)}
                />
              </div>
            </div>
          </div>

          <div className='col-lg-2'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('تاريخ الإسترداد')}</label>
              <Form.Control
                className='form-control text-center'
                type='date'
                name='transactionDate'
                onChange={formikhandleChange}
                value={values.transactionDate || ''}
                isInvalid={!!(touched.transactionDate && errors.transactionDate)}
              />
            </div>
          </div>

          <div className='col-lg-4'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('Note')}</label>
              <Form.Control
                autoComplete='off'
                className='form-control'
                as='textarea'
                rows={1}
                name='description'
                onChange={formik.handleChange}
                value={values.description}
                isInvalid={!!(touched.description && errors.description)}
              />
            </div>
          </div>

          <div className='col-lg-2'>
            <div className='form-group mb-3'>
              <label className='form-label mb-3'>{t('حالة الصرف من المخزن')}</label>
              {data.inventoryFlag ? (
                <span className='badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center'>
                  <i className='fa fa-check-circle fs-15px fa-fw me-5px'></i>
                  {t('تمت الصرف من المخزن')}
                </span>
              ) : (
                <span className='badge border border-danger text-danger px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center'>
                  <i className='fa fa-clock fs-15px fa-fw me-5px'></i>
                  {t('في إنتظار الصرف')}
                </span>
              )}
            </div>
          </div>

          <div className='col-lg-2 text-end mb-2'>
            <button
              type='button'
              className='btn btn-lg me-1 btn-outline-danger mb-1'
              disabled={formik.isSubmitting || data?.inventoryFlag}
              onClick={() => handleDelete()}
            >
              <i className='fa fa-trash fa-fw me-1'></i>
              {t('Delete')}
            </button>
            <ButtonPrint
              PrintTemplateTypeId='eaf14bfb-0a5f-4a88-9bfb-0a09d4c08d38' //
              ReferenceId={data.id || ''}
            />
          </div>
        </div>

        <UpdateReturnLines formik={formik} />

        <div className='text-center'>
          {/* <button disabled={formik.isSubmitting} type='submit' className='btn btn-lg me-1 btn-theme mb-1'>
            {formik.isSubmitting ? (
              <div className='spinner-border spinner-border-sm me-2'></div>
            ) : (
              <i className='fa fa-save fa-fw me-1'></i>
            )}
            {t('Save')}
          </button> */}

          <button type='button' className='btn btn-lg me-1 btn-default mb-1'>
            <i className='fa fa-power-off fa-fw me-1'></i> {t('Cancel')}
          </button>
        </div>
      </Form>
    </div>
  );
};

export default UpdateReturn;
